import Grid from "@material-ui/core/Grid";
import { sumBy, isEmpty, isUndefined } from "lodash";
import moment from "moment";
import "moment-timezone";
import React, { useCallback, useEffect, useState } from "react";
import { t } from "ttag";
import { useStoreActions, useStoreState } from "../../models/RootStore";
import { useConfirmation } from "@coolremote/shared";
// Reuse the BACnet stats presentation components — they are generic table/graph/slider.
import UnitStatsGraph from "../BacnetUnitState/UnitStatsGraph";
import UnitStatsSlider from "../BacnetUnitState/UnitStatsSlider";
import UnitStatsTable from "../BacnetUnitState/UnitStatsTable";
import ModbusEditParamDialog from "./EditParamDialog";
import { engUnitLabel } from "../../screens/Modbus/RegisterTable";
import { MODBUS_REGISTER_WIRE_NAMES } from "../../screens/Modbus/enums";
import useCoolPoll from "@hooks/useCoolPoll";
import { formatToOneDecimal } from "@services/utils";

const MAX_ALLOWED_GAP_PRO = 1000 * 60 * 20; // (20 mins)
const AUTOUPDATE_INTERVAL = 1000 * 60; // one minute
const MAX_ALLOWED_STATS_SELECTIONS = 6;
const paramsColorsSet: any = ["#7a6095", "#35a8e0", "#00b259", "#ef3b2f", "#f8b133", "#7f7182"];

/**
 * Modbus unit statistics panel — mirrors BacnetUnitState/UnitStats.
 * Differences from BACnet:
 *  - stats come from modbusUnits.getModbusUnitsStats (GET /service-params/modbus-units/:id),
 *    assumed to return the same { entries, current, ranges } shape as the BACnet endpoint.
 *  - param metadata (name / eng-units / editable) is read from the unit's embedded
 *    paramTemplateData rows rather than a separately-fetched param template.
 */
const UnitStats: React.FC<any> = ({ modbusUnit, site, minDate }) => {
  const getModbusUnitsStats = useStoreActions((a) => a.modbusUnits.getModbusUnitsStats);
  const { addMessage } = useStoreActions((action) => action.errorMessage);
  const temperatureScale = useStoreState((s) => s.users.me.temperatureScale || 0);
  const { dateFormat, timeFormat } = useStoreState((state) => state.users);
  const [isLoading, setIsLoading] = useState(true);
  const [statDataCollection, setStatDataCollection] = useState<any>([]);
  const [autoupdate, setAutoupdate] = useState(false);
  const [paramsTable, setParamsTable] = useState<any>({});
  const [updateTime, setUpdateTime] = useState<any>("");
  const [unitFetchedData, setUnitFetchedData] = useState<any>({});
  const [paramsColors, setParamsColors] = useState<any>(new Map());
  const [colorSet, setColorSet] = useState<any>(paramsColorsSet);
  const [selectedStartTime, setSelectedStartTime] = useState<any>(new Date().getTime() - 1 * 24 * 60 * 60 * 1000);
  const [selectedEndTime, setSelectedEndTime] = useState<any>(new Date().getTime());
  const confirm = useConfirmation();
  const [disableUpdateBtn, setDisableUpdateBtn] = useState<boolean>(false);
  const [counter, setCounter] = useState<number>(0);
  const dateTimeFormat = `${dateFormat} ${timeFormat}`;
  const timezone = site?.timezone || moment.tz.guess();
  const userPreferences = useStoreState((state) => state.users.userPreferences);
  const updateUserPreferences = useStoreActions((actions) => actions.users.updateUserPreferences);

  // The unit's parameter rows (name / units / register / readOnly) come embedded.
  const unitRows: any[] = modbusUnit?.rows || modbusUnit?.paramTemplateData?.rows || [];
  // Modbus rows have no shortId — stat codes are keyed by register, and arrive
  // either numeric ("2:35") or as the wire name ("hreg:35"), so match both. A
  // bit-packed discrete value reports under its own suffixed code ("hreg:35#1"),
  // distinct from its parent register's ("hreg:35") — match on each row's own
  // bitNumber, same convention as the live operationalData keys.
  const getRowForCode = useCallback(
    (code: any) => {
      const key = String(code).split("/")[0];
      return unitRows.find((r: any) => {
        if (r?.registerAddress == null) {
          return String(r?.shortId) === key || r?.name === key;
        }
        const wireName = MODBUS_REGISTER_WIRE_NAMES[r?.registerType as number];
        const bitSuffix = r?.bitNumber != null ? `#${r.bitNumber}` : "";
        return (
          (r?.registerType != null && `${r.registerType}:${r.registerAddress}${bitSuffix}` === key) ||
          (wireName && `${wireName}:${r.registerAddress}${bitSuffix}` === key) ||
          String(r?.shortId) === key ||
          r?.name === key ||
          String(r?.registerAddress) === key
        );
      });
    },
    [unitRows],
  );

  const prepareUserPrefData = () => {
    if (isEmpty(paramsTable)) {
      return;
    }
    const showingParams = Object.values(paramsTable)
      .filter((row: any) => row?.isShowing || row?.isChecked)
      .reduce((acc: Record<string, { isShowing: boolean; isChecked: boolean }>, row: any) => {
        acc[row.code] = { isShowing: !!row.isShowing, isChecked: !!row.isChecked };
        return acc;
      }, {});

    let userPrefObj = userPreferences?.modbusPreferdParams || {};
    userPrefObj = { ...userPrefObj, [modbusUnit.id]: showingParams };
    updateUserPreferences({ modbusPreferdParams: userPrefObj });
  };

  useEffect(() => {
    if (isLoading) {
      return;
    }
    prepareUserPrefData();
  }, [paramsTable]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setColorSet([...paramsColorsSet]);
  }, [modbusUnit.id]);

  const prepareGraphsData = (statsData: any) => {
    const statDataCollectionTemp: any = [];
    statDataCollectionTemp.push({ timestamp: selectedStartTime, noData: 0.5 });

    (statsData || []).forEach((statPoint: any, statPointIndex: any) => {
      const point = { ...statPoint, noData: 0.5 };
      statDataCollectionTemp.push({ ...point });
      if (
        statsData[statPointIndex + 1] &&
        statsData[statPointIndex + 1].timestamp >= statPoint.timestamp + MAX_ALLOWED_GAP_PRO
      ) {
        statDataCollectionTemp.push({ timestamp: statPoint.timestamp + 2, noData: 0.5 });
      }
    });

    statDataCollectionTemp.push({ timestamp: selectedEndTime, noData: 0.5 });
    setStatDataCollection(statDataCollectionTemp);
  };

  const fetchStatsData = async (id: string, startTime: number, endTime: number) => {
    setIsLoading(true);
    try {
      const statsRes = await getModbusUnitsStats({ modbusUnitId: id, startTime, endTime });
      setUnitFetchedData(statsRes);
      prepareGraphsData(statsRes.entries);
      prepareTableData(statsRes);
    } catch (err: any) {
      addMessage({ message: err.message });
    } finally {
      setIsLoading(false);
      if (autoupdate) {
        if (counter === 59) {
          setAutoupdate(false);
          setCounter(0);
          (async () => {
            const ok = await confirm({
              title: t`Confirm`,
              bodyMessage: "Auto Update has stopped. Do you want to proceed?",
              confirmText: t`Yes`,
              cancelText: t`No`,
            });
            if (ok) {
              setAutoupdate(true);
            }
          })();
        } else {
          setCounter((prevCounter) => prevCounter + 1);
        }
      }
    }
  };

  useEffect(() => {
    if (!modbusUnit || !selectedStartTime || !selectedEndTime) {
      return;
    }
    fetchStatsData(modbusUnit.id, selectedStartTime, selectedEndTime);
  }, [modbusUnit, selectedStartTime, selectedEndTime, temperatureScale]); // eslint-disable-line react-hooks/exhaustive-deps

  const onRefresh = useCallback(() => {
    if (!selectedStartTime) {
      return;
    }
    onDateRangeChange(
      { startDate: new Date(moment(selectedStartTime).tz(timezone).format("llll")), endDate: new Date(moment().tz(timezone).format("llll")) },
      true,
    );
  }, [selectedStartTime]); // eslint-disable-line react-hooks/exhaustive-deps

  const isToday = useCallback((date: any) => {
    const today = new Date(moment().tz(timezone).format("llll"));
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Device-template imports sometimes bake CoolAutomation's default unit-naming
  // convention ("L{line}.{address}", e.g. "L1.203") into a register's name as a
  // disambiguation suffix across the many indoor units one gateway template can
  // cover. Once that register is mapped to a single functional unit, the suffix
  // is redundant here — strip it for display without touching the stored name.
  const stripUnitLocationSuffix = (name: string) => name.replace(/\s+L\d{1,3}\.\d{1,4}$/, "");
  const getParamName = useCallback((code: any) => {
    const name = getRowForCode(code)?.name || code;
    return typeof name === "string" ? stripUnitLocationSuffix(name) : name;
  }, [getRowForCode]);
  const getMeasurementUnits = useCallback((code: any) => engUnitLabel(getRowForCode(code)?.units), [getRowForCode]);
  const getEditableState = useCallback((code: any) => {
    const row = getRowForCode(code);
    return row ? row.readOnly === false : false;
  }, [getRowForCode]);

  const onDateRangeChange = useCallback(async (
    dateRange: { startDate?: Date | undefined; endDate?: Date | undefined },
    skipDateFrameChecking = false) => {
    if (!isUndefined(dateRange.startDate) && !isUndefined(dateRange.endDate)) {
      const timezoneOffset = moment().tz(timezone).utcOffset() * 60 * 1000;
      const checkIsToday = isToday(dateRange.endDate);
      const currentHourMinsArray = moment().tz(timezone).format("HH:mm").split(":");
      const startHoursArray = moment(dateRange.startDate).format("HH:mm").split(":");
      const startTime = Date.UTC(dateRange.startDate.getFullYear(),
        dateRange.startDate.getMonth(), dateRange.startDate.getDate(), +startHoursArray[0], +startHoursArray[1]) - timezoneOffset;
      const endTime = checkIsToday ? Date.UTC(dateRange.endDate.getFullYear(),
        dateRange.endDate.getMonth(), dateRange.endDate.getDate(), +currentHourMinsArray[0], +currentHourMinsArray[1], 0) - timezoneOffset :
        Date.UTC(dateRange.endDate.getFullYear(),
          dateRange.endDate.getMonth(), dateRange.endDate.getDate(), 23, 59, 59) - timezoneOffset;

      if (!isToday(new Date(moment(endTime).tz(timezone).format("llll"))) || (!skipDateFrameChecking && (endTime - startTime > 86400000))) {
        autoupdate && setAutoupdate(false);
        !disableUpdateBtn && setDisableUpdateBtn(true);
        counter && setCounter(0);
      } else {
        disableUpdateBtn && setDisableUpdateBtn(false);
      }

      setSelectedStartTime(startTime);
      setSelectedEndTime(endTime);
    }
  }, [autoupdate, disableUpdateBtn, counter]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportFile = useCallback(async (onlySelected?: boolean) => {
    const checkedKeys = Object.keys(paramsTable).filter(key => paramsTable[key].isChecked);
    const { name = "" } = modbusUnit || {};
    // Use the unit's known parameter set (paramsTable) rather than entries[0]'s keys —
    // a single history row can be a partial snapshot (only the registers read in that
    // poll cycle), so keying off one row can silently drop columns from the export.
    // Fall back to the union of keys across every entry (not just the first) if
    // paramsTable is empty, so a "current"-snapshot gap doesn't zero out the export.
    const entriesForKeys: any[] = unitFetchedData?.entries || [];
    const allEntryKeys: string[] = Array.from(
      entriesForKeys.reduce((keys: Set<string>, entry: any) => {
        Object.keys(entry).forEach((key: string) => keys.add(key));
        return keys;
      }, new Set<string>()),
    ).filter((key: string) => key !== "timestamp" && key !== "day" && key !== "unit_uid" && key !== "device_serial");
    const dataHeaders: string[] = onlySelected ? checkedKeys : !isEmpty(paramsTable) ? Object.keys(paramsTable) : allEntryKeys;
    // Unit/parameter names are free text — quote them so commas/quotes don't break columns.
    const csvField = (val: any) => {
      const str = String(val ?? "");
      return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, "\"\"")}"` : str;
    };
    const headers = ["Date/Time", "Modbus Unit Name", ...dataHeaders.map(code => csvField(getParamName(code)))];
    let rows = headers.join(",") + "\r\n";

    (unitFetchedData?.entries || []).forEach((dataRow: any) => {
      const { timestamp, day, unit_uid, device_serial, ...readings } = dataRow;
      const date = moment(timestamp).tz(timezone).format(`${dateTimeFormat}`);
      const values = dataHeaders.map(code => readings[code]).join(",");
      rows += `${date},${csvField(name)},${values}` + "\r\n";
    });

    // Download via Blob, not a data: URI — encodeURI leaves "#" unescaped, and a "#"
    // in a unit/parameter name turns the rest of a data: URI into a URL fragment,
    // silently truncating the file (header row only, no data rows).
    const blob = new Blob(["\uFEFF" + rows], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = window.document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `${name}.csv`);
    link.click();
    URL.revokeObjectURL(url);
  }, [unitFetchedData, paramsColors, paramsTable, getParamName]); // eslint-disable-line react-hooks/exhaustive-deps

  useCoolPoll({
    frequency: AUTOUPDATE_INTERVAL,
    enabled: autoupdate && !!selectedStartTime,
    onPoll: () => { onRefresh(); },
  });

  const showHideAllParams = useCallback((show: boolean) => {
    const rows = { ...paramsTable };
    Object.keys(rows).forEach((key: any) => {
      const obj = rows[key];
      obj.isShowing = show;
      if (!show) {
        obj.isChecked = false;
      }
    });
    setParamsTable(rows);
  }, [paramsColors, colorSet, paramsTable]);

  const showHideParam = useCallback((code: any) => {
    const oldVal = !!paramsTable[code]?.isShowing;
    if (paramsColors.has(code)) {
      setColorSet([...colorSet, paramsColors.get(code)]);
      paramsColors.delete(code);
      setParamsColors(paramsColors);
    }
    setParamsTable({ ...paramsTable, [code]: { ...paramsTable[code], isShowing: !oldVal, isChecked: false } });
  }, [paramsColors, colorSet, paramsTable]);

  const prepareTableData = async (data: any) => {
    // currentObj may be empty (e.g. unit hasn't reported a live snapshot recently) even
    // when rangesObj/entries have historical data — build the table from rangesObj either
    // way, and just leave live values blank rather than skipping paramsTable entirely.
    const currentObj = data.current;
    const rangesObj = data.ranges;
    const selectedStats: any = userPreferences.modbusPreferdParams || {};
    const newSelected: any = new Map();
    const colorSetTemp = [...paramsColorsSet];
    const tableRows: any = {};
    const unitSupportedParams = !rangesObj
      ? null
      : Object.keys(rangesObj).filter((code) => !!getRowForCode(code));

    if (unitSupportedParams && !isEmpty(unitSupportedParams)) {
      unitSupportedParams.forEach((code: any) => {
        const ranges = rangesObj[code];
        const { min = 0, max = 0 } = ranges;
        const currentValue = currentObj?.[0]?.jdata?.[code];
        let isChecked = false;
        let isShowing = false;

        isShowing = selectedStats[modbusUnit.id] ? !!selectedStats[modbusUnit.id]?.[code]?.isShowing : true;
        const checkedCount = sumBy(tableRows, (row: any) => (row?.isChecked ? 1 : 0));
        if (checkedCount < MAX_ALLOWED_STATS_SELECTIONS) {
          isChecked = selectedStats[modbusUnit.id] ? !!selectedStats[modbusUnit.id]?.[code]?.isChecked : false;
        }
        isShowing && isChecked && newSelected.set(code, colorSetTemp.pop());

        const slider = !isEmpty(ranges) ? <UnitStatsSlider data={{
          code,
          slider: {
            min: formatToOneDecimal(min),
            max: formatToOneDecimal(max),
            selectionMin: formatToOneDecimal(min),
            selectionMax: formatToOneDecimal(max),
            value: formatToOneDecimal(currentValue),
          },
          value: formatToOneDecimal(currentValue),
          name: code,
        }} index={`slider${code}`} /> : null;

        tableRows[code] = {
          code,
          name: getParamName(code),
          measurementUnits: getMeasurementUnits(code),
          value: formatToOneDecimal(currentValue),
          slider,
          plottable: true,
          isShowing,
          isChecked,
          editable: getEditableState(code),
          // Carried through so EditParamDialog can read uiType/customEnum/
          // registerType/registerAddress without re-deriving them.
          rawRow: getRowForCode(code),
        };
      });
    }
    setParamsTable({ ...tableRows });
    setParamsColors(newSelected);
    setUpdateTime(currentObj?.[0]?.timestamp);
    setColorSet(colorSetTemp);
  };

  useEffect(() => {
    const usedColors = Object.values(Object.fromEntries(paramsColors));
    const unUsedColors = paramsColorsSet.filter((color: any) => usedColors.indexOf(color) === -1);
    setColorSet(unUsedColors);
  }, [paramsColors]);

  const updateParamRow = useCallback((code: any) => {
    const oldVal = paramsTable[code].isChecked;
    if (!oldVal && paramsColors.size >= MAX_ALLOWED_STATS_SELECTIONS) {
      return;
    }
    if (!oldVal) {
      const colorSetTemp = colorSet;
      const newColor = colorSetTemp.pop();
      paramsColors.set(code, newColor);
      setColorSet(colorSetTemp);
    } else {
      setColorSet([...colorSet, paramsColors.get(code)]);
      paramsColors.delete(code);
    }
    setParamsColors(new Map(paramsColors));
    setParamsTable({ ...paramsTable, [code]: { ...paramsTable[code], isChecked: !oldVal } });
  }, [paramsTable, colorSet, paramsColors]);

  return (
    <Grid container spacing={0} style={{ padding: "16px 0" }}>
      <Grid item xs={5}>
        <UnitStatsTable
          paramsTable={paramsTable}
          endTime={selectedEndTime}
          isToday={isToday}
          onRefresh={onRefresh}
          isLoading={isLoading}
          updateTime={updateTime}
          autoupdate={autoupdate}
          setAutoupdate={setAutoupdate}
          timezone={timezone}
          updateParamRow={updateParamRow}
          showHideAllParams={showHideAllParams}
          showHideParam={showHideParam}
          disableUpdateBtn={disableUpdateBtn}
          unitParamTemplate={{}}
          bacnetUnit={modbusUnit}
          paramsTitle={t`Modbus Device Parameters`}
          typeTitle={t`Modbus Device`}
          renderEditDialog={(editedRow: any, closeDialog: () => void) => (
            <ModbusEditParamDialog
              row={editedRow}
              modbusUnitId={modbusUnit.id}
              onClose={closeDialog}
              onSaved={() => fetchStatsData(modbusUnit.id, selectedStartTime, selectedEndTime)}
            />
          )}
        />
      </Grid>
      <Grid item={true} xs={7}>
        <UnitStatsGraph
          isToday={isToday}
          startTime={selectedStartTime}
          endTime={selectedEndTime}
          unitStatsData={modbusUnit.id === "all" ? statDataCollection : Object.values(statDataCollection)}
          onDateRangeChange={onDateRangeChange}
          onRefresh={onRefresh}
          isLoading={isLoading}
          exportFile={exportFile}
          enums={modbusUnit?.enums}
          timezone={timezone}
          dateFormat={dateFormat}
          timeFormat={timeFormat}
          hideLegends={isEmpty(paramsTable)}
          paramName={t`Value`}
          paramsColors={paramsColors}
          paramsTable={paramsTable}
          onChipDelete={updateParamRow}
          minDate={minDate}
          unitParamTemplate={{}}
          deviceId={modbusUnit?.id}
        />
      </Grid>
    </Grid>
  );
};

export default UnitStats;
